/**
 * What an agent is doing, told honestly (roadmap D81, D82).
 *
 * Three separate facts, never blended into one status:
 *
 *   session    is there a process: running, stopped, or unknown
 *   activity   what a running agent is doing: working, idle, or unknown —
 *              with what it waits on from a person carried beside it as a
 *              count and a reason, never as a status of its own
 *   resumable  can it come back with its conversation after a restart:
 *              yes, no, or unknown
 *
 * Two diagnoses are worked out from those and the board, at read time, and
 * never stored: **Stuck** (idle, or waiting on a person, while it holds work
 * in progress for longer than the threshold) and **Finished, not yet
 * reviewed** (it is idle with work in review and nothing in progress). An
 * agent whose activity is unknown is never called stuck: unknown is an
 * answer, not a guess.
 *
 * Only types, constants and pure functions live here: the host computes the
 * view (src/acp/agentState.ts), the renderer and the node's dashboard show it.
 */
import { handOffWords } from "./handOff.js";
/**
 * A usage limit is not an error. An agent that reached its plan's allowance is waiting for it to refill (and is
 * restarted when it does), or a person hands its work to another account or runtime. The wire `status` of such a
 * session is still `error` (the board's dispatch sweep skips it for that reason), so no surface may show the raw
 * status for this cause: they read these words instead, which are the same everywhere.
 */
export const WAITING_FOR_LIMIT = "Waiting for its limit";
/** True for a failure that is a usage limit refilling on a clock: the one cause that is a wait, never an error. */
export function isLimitWait(failure) {
    return failure?.cause === "session-limit";
}
/** "lifts in 20 min" under 90 minutes, "lifts 11:40" beyond; nothing when no reset was stated. */
export function limitLiftsWords(resetsAt, now = Date.now()) {
    if (!resetsAt || !Number.isFinite(resetsAt))
        return undefined;
    const ms = resetsAt - now;
    if (ms <= 0)
        return "lifts now";
    if (ms < 90 * 60_000)
        return `lifts in ${Math.max(1, Math.round(ms / 60_000))} min`;
    return `lifts ${new Date(resetsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}
/** "Waiting for its limit · lifts 11:40": the status word of a member that hit its usage limit. */
export function limitWaitingWords(resetsAt, now = Date.now()) {
    const lifts = limitLiftsWords(resetsAt, now);
    return lifts ? `${WAITING_FOR_LIMIT} · ${lifts}` : WAITING_FOR_LIMIT;
}
/** The status word for a session: the limit wait's words when that is what its failure is, else `undefined` (the surface says its own). */
export function limitWaitWordsFor(failure, now = Date.now()) {
    return isLimitWait(failure) ? limitWaitingWords(failure?.resetsAt, now) : undefined;
}
/** The words the surfaces use — plain, the same on every screen. */
export const AGENT_ATTENTION_WORDS = {
    "waiting-for-you": "Waiting for you",
    "waiting-for-limit": WAITING_FOR_LIMIT,
    stuck: "Stuck",
    "finished-unreviewed": "Finished, not yet reviewed",
};
export const CONTEXT_NEARLY_FULL = "Context nearly full";
/** The headline for an agent, or undefined when there is nothing to call out. */
export function agentAttentionWord(state, now = Date.now()) {
    if (state?.attention === "waiting-for-limit")
        return limitWaitingWords(state.limit?.resetsAt, now);
    return state?.attention ? AGENT_ATTENTION_WORDS[state.attention] : undefined;
}
/** One sentence that explains the headline, for a tooltip. */
export function agentStateDetail(state, now = Date.now()) {
    if (!state)
        return undefined;
    const parts = [];
    if (state.needsYou)
        parts.push(`Waiting for you: ${state.needsYou.count} ${state.needsYou.reason}`);
    if (state.limit)
        parts.push(`${limitWaitingWords(state.limit.resetsAt, now)}. It is restarted when the limit lifts, or its work can be handed to another account or runtime`);
    if (state.handOff)
        parts.push(`Running ${handOffWords(state.handOff)}`);
    if (state.stuck) {
        const minutes = Math.max(1, Math.round((now - state.stuck.since) / 60_000));
        parts.push(`Stuck: no progress for ${minutes} min while holding ${state.stuck.taskIds.join(", ")}`);
    }
    if (state.unreviewed)
        parts.push(`Finished, not yet reviewed: ${state.unreviewed.taskIds.join(", ")}`);
    if (state.context)
        parts.push(`${CONTEXT_NEARLY_FULL}: ${state.context.percent}% used${state.context.compaction === "command" ? ", it will be compacted" : state.context.compaction === "restart" ? ", its conversation will be restarted with a handoff note" : ""}`);
    if (state.resumable === "no" && state.session === "running")
        parts.push("Cannot resume its conversation after a restart");
    return parts.length ? parts.join(". ") + "." : undefined;
}
//# sourceMappingURL=agentState.js.map